// Sample page: 3 design variants for the at-a-glance "stay vitals" strip.
// Mocked with For A While data. Preview-only route: /sample/vitals
import React from 'react';

const MOCK = {
  display_name: 'For A While',
  region: 'Bathad, Tirthan Valley',
  state: 'Himachal Pradesh',
  unit_count: 2,
  unit_type: 'cottages',
  starting_price_inr: 5500,
  price_includes: 'with breakfast',
  sleeps_max: 4,
  altitude_meters: 1500,
  property_type: 'Homestay',
  best_months: [3, 4, 5, 6, 9, 10, 11],
  best_for: ['Couples', 'Foodies', 'Families', 'Slow travellers'],
  features: [
    { icon: '🔥', label: 'Wood stove' },
    { icon: '🍳', label: 'Breakfast included' },
    { icon: '🅿️', label: 'Parking' },
    { icon: '🐕', label: 'Pet-friendly' },
    { icon: '🎣', label: 'River fishing' },
  ],
};

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const fmtSeason = (months) => {
  if (!months?.length) return 'Year-round';
  const runs = [];
  let s = months[0];
  let p = s;
  for (let i = 1; i < months.length; i++) {
    if (months[i] === p + 1) { p = months[i]; continue; }
    runs.push([s, p]); s = p = months[i];
  }
  runs.push([s, p]);
  return runs.map(([a, b]) => a === b ? MONTHS[a - 1] : `${MONTHS[a - 1]}–${MONTHS[b - 1]}`).join(', ');
};
const fmtINR = (n) => '₹' + (n || 0).toLocaleString('en-IN');

// ─────────────────────────────────────────────────────────────
// VARIANT A — Editorial trio: huge serif numerals, hairline dividers
// ─────────────────────────────────────────────────────────────
const VariantA = ({ d }) => (
  <div className="bg-stone-50 border-y border-stone-200 py-12 px-6 md:px-16">
    <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-0 md:divide-x divide-stone-300">
      <div className="md:px-10 text-center md:text-left">
        <div className="text-[11px] uppercase tracking-[0.22em] text-stone-500 mb-2">The Property</div>
        <div className="font-serif text-5xl md:text-6xl text-stone-900 leading-none">
          {d.unit_count}
        </div>
        <div className="text-sm tracking-wide text-stone-700 mt-2 uppercase">{d.unit_type}</div>
      </div>
      <div className="md:px-10 text-center md:text-left">
        <div className="text-[11px] uppercase tracking-[0.22em] text-stone-500 mb-2">Starting from</div>
        <div className="font-serif text-5xl md:text-6xl text-stone-900 leading-none">
          {fmtINR(d.starting_price_inr)}
        </div>
        <div className="text-sm tracking-wide text-stone-700 mt-2 italic">{d.price_includes}</div>
      </div>
      <div className="md:px-10 text-center md:text-left">
        <div className="text-[11px] uppercase tracking-[0.22em] text-stone-500 mb-2">Sleeps up to</div>
        <div className="font-serif text-5xl md:text-6xl text-stone-900 leading-none">
          {d.sleeps_max}
        </div>
        <div className="text-sm tracking-wide text-stone-700 mt-2 uppercase">Guests</div>
      </div>
    </div>
    {/* Tier 2 secondary row */}
    <div className="max-w-5xl mx-auto mt-10 pt-6 border-t border-stone-200 flex flex-wrap gap-x-10 gap-y-3 text-sm text-stone-600 justify-center md:justify-start">
      <span><span className="text-stone-400 uppercase tracking-wider text-[10px] mr-2">Altitude</span>{d.altitude_meters} m</span>
      <span><span className="text-stone-400 uppercase tracking-wider text-[10px] mr-2">Property</span>{d.property_type}</span>
      <span><span className="text-stone-400 uppercase tracking-wider text-[10px] mr-2">Best</span>{fmtSeason(d.best_months)}</span>
      <span><span className="text-stone-400 uppercase tracking-wider text-[10px] mr-2">Ideal for</span>{d.best_for.slice(0, 3).join(' · ')}</span>
    </div>
  </div>
);

// ─────────────────────────────────────────────────────────────
// VARIANT B — Dark "ledger" card: cream-on-charcoal, monogrammed
// ─────────────────────────────────────────────────────────────
const VariantB = ({ d }) => (
  <div className="py-16 px-6 md:px-16 bg-stone-50">
    <div className="max-w-5xl mx-auto">
      <div className="bg-[#1a1816] text-stone-100 rounded-sm shadow-2xl overflow-hidden">
        <div className="grid grid-cols-3 divide-x divide-stone-700">
          {[
            { label: 'Stay capacity', big: d.unit_count, sub: d.unit_type.toUpperCase() },
            { label: 'From / night', big: fmtINR(d.starting_price_inr), sub: d.price_includes.toUpperCase() },
            { label: 'Sleeps', big: d.sleeps_max, sub: 'GUESTS · MAX' },
          ].map((c, i) => (
            <div key={i} className="px-6 md:px-10 py-10 md:py-12">
              <div className="text-[10px] uppercase tracking-[0.3em] text-amber-200/70 mb-3">{c.label}</div>
              <div className="font-serif text-5xl md:text-7xl text-stone-50 leading-none tracking-tight">{c.big}</div>
              <div className="mt-3 text-xs tracking-widest text-stone-400">{c.sub}</div>
            </div>
          ))}
        </div>
        <div className="bg-[#0f0e0c] px-6 md:px-10 py-5 flex flex-wrap gap-x-8 gap-y-2 text-[11px] tracking-wider text-stone-300 uppercase">
          <span><span className="text-amber-200/60 mr-2">▲</span>{d.altitude_meters} m</span>
          <span><span className="text-amber-200/60 mr-2">◆</span>{d.property_type}</span>
          <span><span className="text-amber-200/60 mr-2">☀</span>{fmtSeason(d.best_months)}</span>
          <span className="hidden md:inline"><span className="text-amber-200/60 mr-2">♥</span>{d.best_for.slice(0, 3).join(' · ')}</span>
        </div>
      </div>
    </div>
  </div>
);

// ─────────────────────────────────────────────────────────────
// VARIANT C — Magazine-spread: asymmetric, single-row, super-large hero number
// ─────────────────────────────────────────────────────────────
const VariantC = ({ d }) => (
  <div className="bg-amber-50/40 py-16 px-6 md:px-16 border-y border-amber-900/10">
    <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-end">
      <div className="md:col-span-4">
        <div className="text-[11px] uppercase tracking-[0.25em] text-amber-900/60">A stay of</div>
        <div className="flex items-baseline gap-3 mt-2">
          <div className="font-serif text-7xl md:text-[7rem] text-stone-900 leading-none">{d.unit_count}</div>
          <div className="text-2xl md:text-3xl font-serif text-stone-700 italic">{d.unit_type}</div>
        </div>
        <div className="mt-4 text-sm text-stone-600">
          Sleeps <span className="font-semibold text-stone-900">{d.sleeps_max}</span> · {d.property_type}
        </div>
      </div>
      <div className="md:col-span-4 md:border-l md:pl-12 border-amber-900/15">
        <div className="text-[11px] uppercase tracking-[0.25em] text-amber-900/60">From</div>
        <div className="font-serif text-6xl md:text-7xl text-stone-900 leading-none mt-2">
          {fmtINR(d.starting_price_inr)}
        </div>
        <div className="mt-3 text-sm tracking-wide text-stone-600 italic">{d.price_includes} · per night</div>
      </div>
      <div className="md:col-span-4 md:border-l md:pl-12 border-amber-900/15">
        <div className="text-[11px] uppercase tracking-[0.25em] text-amber-900/60 mb-3">Good to know</div>
        <ul className="space-y-2 text-sm text-stone-700">
          <li><span className="text-stone-400 inline-block w-20 text-xs uppercase tracking-wider">Altitude</span>{d.altitude_meters} m</li>
          <li><span className="text-stone-400 inline-block w-20 text-xs uppercase tracking-wider">Season</span>{fmtSeason(d.best_months)}</li>
          <li><span className="text-stone-400 inline-block w-20 text-xs uppercase tracking-wider">Ideal</span>{d.best_for.slice(0, 3).join(', ')}</li>
        </ul>
      </div>
    </div>
    {/* Feature icon row */}
    <div className="max-w-6xl mx-auto mt-10 pt-6 border-t border-amber-900/10 flex flex-wrap gap-6 justify-center md:justify-start">
      {d.features.map((f, i) => (
        <div key={i} className="flex items-center gap-2 text-xs text-stone-600">
          <span className="text-lg">{f.icon}</span>
          <span className="tracking-wide">{f.label}</span>
        </div>
      ))}
    </div>
  </div>
);

export default function SampleVitals() {
  return (
    <div className="min-h-screen bg-white">
      <div className="bg-stone-900 text-stone-100 py-10 px-6 md:px-16">
        <div className="max-w-5xl mx-auto">
          <div className="text-[11px] uppercase tracking-[0.3em] text-amber-200/60">Internal preview</div>
          <h1 className="font-serif text-3xl md:text-4xl mt-2">Stay Vitals — 3 variants</h1>
          <p className="text-stone-400 text-sm mt-3 max-w-2xl">
            At-a-glance fact strip for every stay page. Sample data: For A While Cottages.
            Tier 1 surfaces <b className="text-stone-100">cottages / price / sleeps</b>. Tier 2 adds altitude, season, property type, ideal-for.
            Tier 3 (Variant C only) shows amenity icons.
          </p>
        </div>
      </div>

      <section>
        <div className="px-6 md:px-16 pt-12 pb-2 max-w-5xl mx-auto">
          <div className="text-[10px] uppercase tracking-[0.3em] text-amber-700 mb-1">Variant A</div>
          <h2 className="font-serif text-2xl text-stone-900">Editorial Trio — light, restrained, hairline dividers</h2>
        </div>
        <VariantA d={MOCK} />
      </section>

      <section className="mt-8">
        <div className="px-6 md:px-16 pt-12 pb-2 max-w-5xl mx-auto">
          <div className="text-[10px] uppercase tracking-[0.3em] text-amber-700 mb-1">Variant B</div>
          <h2 className="font-serif text-2xl text-stone-900">Charcoal Ledger — dramatic, cream-on-dark, premium</h2>
        </div>
        <VariantB d={MOCK} />
      </section>

      <section className="mt-8">
        <div className="px-6 md:px-16 pt-12 pb-2 max-w-5xl mx-auto">
          <div className="text-[10px] uppercase tracking-[0.3em] text-amber-700 mb-1">Variant C</div>
          <h2 className="font-serif text-2xl text-stone-900">Magazine Spread — asymmetric, hero numeral, amenities row</h2>
        </div>
        <VariantC d={MOCK} />
      </section>

      <div className="py-20 text-center text-stone-400 text-sm">
        End of sample. Pick a variant or ask for tweaks.
      </div>
    </div>
  );
}
